/**
 * What a company's short name and sign-in domains may be.
 *
 * The same rules the API applies, written here so the form can answer while
 * she is typing instead of after she presses the button. The API still checks
 * every one of them: this copy is for speed, not for safety, and nothing
 * relies on it having run.
 */

const RESERVED = new Set([
  'stello-ops', 'admin', 'api', 'auth', 'stello', 'system', 'internal', 'support', 'root', 'www',
])

export function checkSlug(slug: string): string | null {
  if (!slug) return 'A short name is needed.'
  if (slug.length < 3) return 'A short name needs at least three characters.'
  if (slug.length > 30) return 'A short name can be at most thirty characters.'
  // The hash is the one that matters. A login is the short name, then a hash,
  // then the email, and the company is read off as far as the first hash. A
  // short name containing one makes a login for a company nobody has.
  if (slug.includes('#')) {
    return 'A short name cannot contain a #, because that character separates the company from the email in every login.'
  }
  if (!/^[a-z0-9-]{3,30}$/.test(slug)) {
    return 'A short name can only use lower case letters, numbers and hyphens.'
  }
  if (RESERVED.has(slug)) return 'That short name is kept for the system.'
  return null
}

/** Suggests one from the company name. She can always type over it. */
export const slugFrom = (name: string) =>
  name.toLowerCase().trim().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 30)

/** The same domain typed two ways is the same domain. */
export const tidyDomain = (raw: string) =>
  raw.trim().toLowerCase().replace(/^@/, '').replace(/^https?:\/\//, '').replace(/\/.*$/, '')

export function checkDomain(domain: string): string | null {
  if (!domain) return 'An email domain is needed.'
  if (!/^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/.test(domain)) {
    return `"${domain}" does not look like an email domain.`
  }
  return null
}

/** The pool's password rules, so the first sign in screen can tick them off. */
export const PASSWORD_RULES = [
  { label: 'At least twelve characters', ok: (p: string) => p.length >= 12 },
  { label: 'A capital letter', ok: (p: string) => /[A-Z]/.test(p) },
  { label: 'A small letter', ok: (p: string) => /[a-z]/.test(p) },
  { label: 'A number', ok: (p: string) => /[0-9]/.test(p) },
]

export function weakPassword(password: string): string | null {
  const missed = PASSWORD_RULES.find((r) => !r.ok(password))
  return missed ? `${missed.label} is needed.` : null
}

/** How long a password handed out by a super admin keeps working. */
export const TEMPORARY_DAYS = 3
